/**
 * A small IndexedDB helper: one connection per database, two shapes of store.
 *
 * The workspace is one JSON blob per key, written whole — that was M2's entire
 * storage requirement and it does not justify a dependency, since Dexie and idb
 * both weigh more than this file and bring query builders nothing here needs.
 *
 * M3 adds the second shape the header of this file predicted: an **append-only
 * log** for the sync outbox. It is an extra object store in the same database,
 * not a different library, and it is why the database is now opened at version
 * {@link DATABASE_VERSION} with the full store list declared in one place —
 * both callers share a connection, so neither can open at a version the other
 * has already superseded.
 */

/** One object store in the schema. */
interface StoreSpec {
  readonly name: string;
  /** Append-only stores get browser-assigned monotonic keys. */
  readonly autoIncrement?: boolean;
}

/**
 * Bumped when {@link DATABASE_STORES} changes.
 *
 * v1 → v2 added `outbox`. `onupgradeneeded` only creates missing stores, so an
 * existing user's saved workspace survives the bump untouched.
 */
export const DATABASE_VERSION = 2;

/** Every object store, for every caller. Declared once so upgrades stay coherent. */
export const DATABASE_STORES: readonly StoreSpec[] = [
  { name: 'workspace' },
  { name: 'outbox', autoIncrement: true },
];

/**
 * How long an `indexedDB.open()` gets before it is treated as never answering.
 *
 * Not a slow-disk guard — a floor under a browser bug. Chromium can leave an
 * open request **unsettled**: no `success`, no `error`, not even `blocked`, when
 * a document is torn down while the next one is opening the same database, which
 * is what a burst of rapid same-origin navigations produces. Because the open is
 * memoised below, one such request poisons every caller in the document at once
 * — the snapshot read, the debounced writer and the sync outbox all await the
 * same promise, and none of them has an error path to take, because nothing ever
 * fails. They simply stop.
 *
 * A rejection is something every one of them already knows how to handle:
 * `persistence.load()` returns `null` through `onError`, `write()` reports and
 * carries on, and `SyncClient.start()` surfaces it rather than hanging on its
 * first `await`. So the fix is to make the stall *fail* — which is also why the
 * memoised entry is dropped on rejection, so the next call opens fresh instead
 * of inheriting the corpse.
 *
 * Ten seconds is deliberately generous: a genuine cold open on a loaded machine
 * with a large database is well under a second, and the cost of being wrong here
 * is a spurious "storage unavailable" session, so the number is set far past any
 * real open rather than tuned close to one.
 *
 * It also sits **after** `HYDRATION_TIMEOUT_MS` (4 s, `persistence.ts`), and that
 * ordering is the intended one: the boot never waits on this timeout, because
 * the workspace binding has already given up at 4 s and opened an empty
 * workspace, so the splash is bounded by the shorter number. This one is what
 * unsticks everything behind the splash — the outbox, the socket, the writer —
 * six seconds later, instead of never.
 */
export const OPEN_TIMEOUT_MS = 10_000;

/** The minimal key/value surface the persistence layer uses. Swappable in tests. */
export interface KeyValueStore {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
}

/** One entry of an append-only store. */
export interface LogEntry<T> {
  /** Browser-assigned, strictly increasing for the life of the store. */
  readonly key: number;
  readonly value: T;
}

/** An append-only store: push to the back, read in order, delete by key. */
export interface AppendLogStore {
  /** Append one value; resolves with its assigned key. */
  append(value: unknown): Promise<number>;
  /** Every entry, oldest first. */
  all<T>(): Promise<readonly LogEntry<T>[]>;
  /** Remove specific entries. Unknown keys are ignored. */
  remove(keys: readonly number[]): Promise<void>;
  /** Remove everything. */
  clear(): Promise<void>;
}

/** Wrap an `IDBRequest` in a promise. */
function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/**
 * Open (or create) a database with every store in {@link DATABASE_STORES}.
 *
 * Memoised per `(factory, databaseName)`: a burst of debounced writes and the
 * outbox's appends share one handle, and nothing races to upgrade. Sharing is
 * also why the open is bounded — see {@link OPEN_TIMEOUT_MS}, since one stalled
 * request otherwise stalls every caller in the document at once.
 */
const connections = new WeakMap<IDBFactory, Map<string, Promise<IDBDatabase>>>();

function connect(databaseName: string, indexedDb: IDBFactory | undefined): Promise<IDBDatabase> {
  if (indexedDb === undefined) return Promise.reject(new Error('IndexedDB is unavailable'));

  let byName = connections.get(indexedDb);
  if (byName === undefined) {
    byName = new Map();
    connections.set(indexedDb, byName);
  }
  const existing = byName.get(databaseName);
  if (existing !== undefined) return existing;

  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDb.open(databaseName, DATABASE_VERSION);
    /** Set when the timeout won: this request's result is no longer wanted. */
    let abandoned = false;

    const giveUp = setTimeout(() => {
      abandoned = true;
      reject(new Error(`IndexedDB open did not settle within ${OPEN_TIMEOUT_MS} ms`));
    }, OPEN_TIMEOUT_MS);

    open.onupgradeneeded = () => {
      for (const spec of DATABASE_STORES) {
        if (open.result.objectStoreNames.contains(spec.name)) continue;
        open.result.createObjectStore(
          spec.name,
          spec.autoIncrement === true ? { autoIncrement: true } : undefined,
        );
      }
    };
    open.onsuccess = () => {
      clearTimeout(giveUp);
      // Turned up after we gave up on it. Nothing is waiting for this handle any
      // more — but an open connection holds a lock that blocks the next version
      // change, and by now a second connection to the same database has very
      // likely been made in its place. Close it rather than leak it.
      if (abandoned) {
        open.result.close();
        return;
      }
      resolve(open.result);
    };
    // Rejecting an already-rejected promise is a no-op, so the abandoned case
    // needs no guard here.
    open.onerror = () => {
      clearTimeout(giveUp);
      reject(open.error ?? new Error('IndexedDB open failed'));
    };
    open.onblocked = () => {
      clearTimeout(giveUp);
      reject(new Error('IndexedDB open blocked by another connection'));
    };
  });
  // A failed open must not be cached, or the tab never recovers — which now
  // includes one that timed out. Only if it is still the cached entry: by the
  // time a stalled open rejects, a later call may already have installed its
  // replacement, and evicting *that* would throw away a working connection.
  opening.catch(() => {
    if (byName.get(databaseName) === opening) byName.delete(databaseName);
  });
  byName.set(databaseName, opening);
  return opening;
}

async function transact<T>(
  databaseName: string,
  storeName: string,
  indexedDb: IDBFactory | undefined,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await connect(databaseName, indexedDb);
  const transaction = database.transaction(storeName, mode);
  const result = await request(run(transaction.objectStore(storeName)));
  // Read transactions are done once the request resolves; writes need the
  // commit to land before the caller can claim the data is durable.
  if (mode === 'readwrite') {
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Write failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Write aborted'));
    });
  }
  return result;
}

/** A key/value handle on one object store. */
export function openKeyValueStore(
  databaseName: string,
  storeName: string,
  indexedDb: IDBFactory | undefined = globalThis.indexedDB,
): KeyValueStore {
  const run = <T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => IDBRequest<T>) =>
    transact(databaseName, storeName, indexedDb, mode, body);

  return {
    get: <T>(key: string) => run<T | undefined>('readonly', (store) => store.get(key)),
    set: (key, value) => run('readwrite', (store) => store.put(value, key)).then(() => undefined), // prettier-ignore
    delete: (key) => run('readwrite', (store) => store.delete(key)).then(() => undefined),
  };
}

/**
 * An append-only handle on one object store.
 *
 * `all()` reads with a cursor rather than `getAll()` so the browser-assigned
 * keys come back with their values — the outbox needs them to prune exactly the
 * entries the server acked, and nothing else.
 */
export function openAppendLog(
  databaseName: string,
  storeName: string,
  indexedDb: IDBFactory | undefined = globalThis.indexedDB,
): AppendLogStore {
  const run = <T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => IDBRequest<T>) =>
    transact(databaseName, storeName, indexedDb, mode, body);

  return {
    append: (value) =>
      run<IDBValidKey>('readwrite', (store) => store.add(value)).then((key) => Number(key)),

    async all<T>() {
      const database = await connect(databaseName, indexedDb);
      const transaction = database.transaction(storeName, 'readonly');
      const cursorRequest = transaction.objectStore(storeName).openCursor();
      return new Promise<readonly LogEntry<T>[]>((resolve, reject) => {
        const entries: LogEntry<T>[] = [];
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor === null) {
            resolve(entries);
            return;
          }
          entries.push({ key: Number(cursor.key), value: cursor.value as T });
          cursor.continue();
        };
        cursorRequest.onerror = () =>
          reject(cursorRequest.error ?? new Error('IndexedDB cursor failed'));
      });
    },

    async remove(keys) {
      if (keys.length === 0) return;
      const database = await connect(databaseName, indexedDb);
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      for (const key of keys) store.delete(key);
      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('Delete failed'));
        transaction.onabort = () => reject(transaction.error ?? new Error('Delete aborted'));
      });
    },

    clear: () => run('readwrite', (store) => store.clear()).then(() => undefined),
  };
}
